import CustomContainer from "../../components/common/CustomContainer";
import styles from "../../assets/styles/appointments.module.css";
import { FormGroup, FormLabel, InputAdornment, MenuItem, Select, TextField } from "@mui/material";
import { FormEvent, useEffect, useState } from "react";
import PreEmploymentTable from "../../components/healthChecks/PreEmploymentTable";
import { addPreEmployeeApi } from "../../utils/api/services";
import { SearchIcon } from "../../utils/functions/Icons";
import { toast } from "react-toastify";
import { getPreEmployeeReducer } from "../../utils/store/utilization/utilizationSlice";
import { getCiyReducer, getStateReducer } from "../../utils/store/profile/ProfileSlice";
import { calculateAge } from "../../utils/functions/common";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../utils/store";
import AddEmployModal from "../../components/healthChecks/AddEmployModal";
import Loader from "../../components/common/Loader";
import CustomPieChart from "../../components/common/CustomPieChart";
import { DatePicker } from "@mui/x-date-pickers";
import { useModuleAccess } from "../../components/hooks/useModuleAccess";
import allModules from "../../utils/functions/modules";

type Status = "Scheduled" | "Completed" | "Pending" | "Cancelled" | "Rescheduled" | "Report Generated";

const HealthCheckups = () => {
    const [timeFilter, setTimeFilter] = useState("Week");
    const [searchValue, setSearchValue] = useState('');
    const [status, setStatus] = useState<string | number>("0");
    const [openAddModal, setOpenAddModal] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [formStates, setFormStates] = useState<PreEmploymentFormTypes>({
        name: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        gender: '',
        currentLocation: '',
        age: '',
        city: '',
        state: '',
        zipCode: '',
        type: '',
        health_check_up_date: '',
    });
    const [filterData, setFilterData] = useState<filterTypes>({
        from_date: '',
        to_date: ''
    })
    const [PreEData, setPreEData] = useState<PieDataTypes[] | []>([]);
    const [anualEData, setAnualEData] = useState<PieDataTypes[] | []>([]);
    const { stateData, cityData } = useSelector((state: RootState) => state.profile);
    const { preEmployeeData, preEmployLoading } = useSelector((state: RootState) => state.utilization);
    const { LoginData } = useSelector((state: RootState) => state.auth);
    const dispatch = useDispatch<AppDispatch>();
    const healthCheckup = useModuleAccess(allModules.healthCheckup)
    const closeAddModal = () => {
        setOpenAddModal(false);
    };

    const handleInputChange: HandleInputChangeType = (e) => {
        const { name, value } = e.target;
        setFormStates({ ...formStates, [name]: value });
    };

    const handleSelectChange: HandleSelectChangeType = (e) => {
        const { name, value } = e.target;
        setFormStates({ ...formStates, [name]: value });
        if (name === "state") {
            const stateCode = stateData?.filter((item) => item?.id === value);
            if (stateCode?.length > 0) {
                dispatch(getCiyReducer({ state_id: stateCode[0]?.id }));
            }
        }
    };

    const handleDateChange: HandleDateChangeType = (date, name) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        setFormStates({ ...formStates, [name]: formattedDate });
        if (name === 'dateOfBirth') {
            const age = calculateAge(formattedDate);
            setFormStates((prev) => ({ ...prev, age: age?.toString() }));
        }
    };

    const handleDateFilters: HandleDateChangeType = (date, name) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        if (!date) {
            setFilterData({ ...filterData, [name]: '' });
        }
        else {
            setFilterData({ ...filterData, [name]: formattedDate });
        }
    };

    useEffect(() => {
        if (LoginData) {
            if (searchValue) {
                const cleanFunction = setTimeout(() => {
                    dispatch(getPreEmployeeReducer({ companyId: LoginData?.companyId, search: searchValue, status: status, from_date: filterData?.from_date, to_date: filterData?.to_date }));
                }, 1000);
                return () => clearTimeout(cleanFunction);
            }
            else (
                dispatch(getPreEmployeeReducer({ companyId: LoginData?.companyId, search: searchValue, status: status, from_date: filterData?.from_date, to_date: filterData?.to_date }))
            )
        }
    }, [searchValue, status, LoginData, dispatch, filterData]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setBtnLoading(true);
            const sendData = {
                name: formStates?.name,
                email: formStates?.email,
                phone: formStates?.phone,
                dateofbirth: formStates?.dateOfBirth,
                gender: formStates?.gender,
                age: formStates?.age,
                city: formStates?.city,
                state: formStates?.state,
                zip_code: formStates?.zipCode,
                type: formStates?.type,
                companyId: LoginData?.companyId,
                health_check_up_date: formStates?.health_check_up_date,
            };
            const result = await addPreEmployeeApi(sendData);
            if (result?.success) {
                toast.success(result?.message);
                setOpenAddModal(false);
                if (LoginData) {
                    dispatch(getPreEmployeeReducer({ companyId: LoginData?.companyId, search: searchValue, status: status }));
                }
                setFormStates({
                    name: '',
                    email: '',
                    phone: '',
                    dateOfBirth: '',
                    gender: '',
                    currentLocation: '',
                    age: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    type: '',
                    health_check_up_date: '',
                })
            }
        } catch (error: any) {
            toast.error(error?.data?.errors || error?.message || "An unexpected error occurred.");
        } finally {
            setBtnLoading(false);
        }
    };

    useEffect(() => {
        dispatch(getStateReducer());
    }, [])

    useEffect(() => {
        if (preEmployeeData?.statusWise?.preEnroll) {
            const statusColorMapping: Record<Status, string> = {
                "Scheduled": "#80B628",
                "Completed": "#54a276",
                "Pending": "#ffcd29",
                "Cancelled": "#aa2626",
                "Rescheduled": "#FF9800",
                "Report Generated": "#2196F3",
            };
            const preEData = preEmployeeData.statusWise?.preEnroll.map((item) => ({
                name: item.status,
                value: item.count,
                color: statusColorMapping[item.status as Status] || "#0562cc"
            }));
            const anualEData = preEmployeeData.statusWise?.anual.map((item) => ({
                name: item.status,
                value: item.count,
                color: statusColorMapping[item.status as Status] || "#0562cc"
            }));

            setPreEData(preEData);
            setAnualEData(anualEData);
        }
    }, [preEmployeeData]);
    return (
        <CustomContainer>
            <div className={'AllPageMain'}>
                <div className={'HeadAndButton'}>
                    <h2>Health checkup</h2>
                </div>
                <div className="flex justify-end items-center gap-6">
                    <FormGroup className="customFormGroup">
                        <FormLabel className="customFormLabel">From Date</FormLabel>
                        <DatePicker
                            className="custumTextField profileInput"
                            value={filterData?.from_date ? moment(filterData?.from_date, 'YYYY-MM-DD') : null}
                            maxDate={moment(new Date(), 'YYYY-MM-DD')}
                            format="YYYY-MM-DD"
                            onChange={(date) => handleDateFilters(date ? moment(date)?.toDate() : null, 'from_date')}
                            slotProps={{ field: { clearable: filterData?.from_date ? true : false } }}
                        />
                    </FormGroup>
                    <FormGroup className="customFormGroup">
                        <FormLabel className="customFormLabel">To Date</FormLabel>
                        <DatePicker
                            className="custumTextField profileInput"
                            value={filterData?.to_date ? moment(filterData?.to_date, 'YYYY-MM-DD') : null}
                            maxDate={moment(new Date(), 'YYYY-MM-DD')}
                            minDate={moment(filterData?.from_date, 'YYYY-MM-DD')}
                            disabled={filterData?.from_date === "" ? true : false}
                            format="YYYY-MM-DD"
                            onChange={(date) => handleDateFilters(date ? moment(date)?.toDate() : null, 'to_date')}
                            slotProps={{ field: { clearable: filterData?.from_date ? true : false } }}
                        />
                    </FormGroup>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 my-6">
                    {PreEData.length > 0 && (
                        <CustomPieChart chartData={PreEData} title={"Pre-employment"} />
                    )}
                    {anualEData.length > 0 && (
                        <CustomPieChart chartData={anualEData} title={"Annual"} />
                    )}
                </div>
                <div className={`${styles.appointmentsMain}`}>
                    {
                        healthCheckup?.canEdit &&
                        <div className='flex justify-end my-6'>
                            <button className='fillButton' onClick={() => setOpenAddModal(true)}>Add employee +</button>
                        </div>
                    }
                    <div className={'mb-6 flex justify-between items-center flex-col md:flex-row w-full max-w-full gap-y-4 md:gap-y-0'}>
                        <FormGroup className="customFormGroup md:w-1/2 w-full">
                            <TextField
                                className={`custumTextField ${styles.InputStyle}`}
                                id="searchValue"
                                name="searchValue"
                                onChange={(e) => setSearchValue(e.target.value)}
                                value={searchValue}
                                placeholder='search'
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormGroup>
                        <FormGroup className="customFormGroup md:w-36 w-full">
                            <Select
                                className={`custumTextField profileInput`}
                                placeholder="Checkup status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <MenuItem value="0">All</MenuItem>
                                <MenuItem value="1">Scheduled</MenuItem>
                                <MenuItem value="2">Completed</MenuItem>
                                <MenuItem value="3">Pending</MenuItem>
                                <MenuItem value="4">Cancelled</MenuItem>
                                <MenuItem value="5">Rescheduled</MenuItem>
                            </Select>
                        </FormGroup>
                    </div>
                    {preEmployLoading ? <Loader /> : <PreEmploymentTable userDetails={preEmployeeData?.userDetails} />}
                </div>
            </div>
            <AddEmployModal
                openAddModal={openAddModal}
                closeAddModal={closeAddModal}
                handleSubmit={handleSubmit}
                btnLoading={btnLoading}
                title={`Add Employee`}
                formStates={formStates}
                handleInputChange={handleInputChange}
                handleDateChange={handleDateChange}
                handleSelectChange={handleSelectChange}
                stateData={stateData}
                cityData={cityData}
            />
        </CustomContainer>
    );
};

export default HealthCheckups;